// @flow
import { createSelector } from "reselect";
import {
  filter,
  fromPairs,
  map,
  omit,
  pick,
  pipe,
  sortBy,
  toPairs,
  pathOr,
} from "ramda";
import { Maybe } from "ramda-fantasy";

import { hash } from "../../util";

import {
  type ConfiguracaoDeRelatorio,
  type ConfiguracaoGlobalRelatorio,
  MODO_APP_SERIES,
  FORMATO_DATA_BANCO,
} from "../../model";

import { type State } from "../";
import { getTipoDataBaseSelecionada, getModoApp } from "./comum";
import { getDataBaseFormatadaBanco } from "./dataBaseSelectors";
import {
  getPeriodosSelecionados,
  getPeriodicidadeSelecionada,
  getAjusteDiasSelecionado,
  getDataInicialSelecionada,
  getDataFinalSelecionada,
} from "./edicaoMedidaSelectors";
import { getMedidasSelecionadas } from "./medidaSelecionadaSelectors";
import { selectedReport } from "../resultado";
import {
  exibirDatasSemNegociacaoSeries,
  identificadoresSeries,
} from "./series";

const normalizaMedida = medida => ({
  ...pick(["codigoOriginal", "nome"], medida),
  medidasSecundarias: Maybe(medida.medidasSecundarias)
    .map(map(normalizaMedida))
    .getOrElse(null),
});

const normalizaMedidaSelecionada = pipe(
  ({ medida, ...medidaSelecionada }) => ({
    ...medidaSelecionada,
    medida: normalizaMedida(medida),
  }),
  ({ label, ...medidaSelecionada }) => ({
    ...medidaSelecionada,
    // Necessário para medidas do tipo ranking, onde o label retornando no resultado
    // tem espaços em branco a mais ou a menos que a medida original
    label: (label || "").replace(/\s+/g, " "),
  }),
  omit([
    "isChecked",
    "buscaGlobal",
    "dataBase",
    "descricaoSelecao",
    "formatoExibicao",
    "formatoOriginal",
    "modoExibicaoMedida",
    "nomeMedida",
    "indiceMedidaEditada",
  ]),
  toPairs,
  filter(([_, val]) => val != null),
  sortBy(([key]) => key),
  fromPairs
);

const medidasLimpas = createSelector(
  getMedidasSelecionadas,
  map(omit(["isChecked"]))
);

const ifModoSeries = (modo, valor) =>
  modo === MODO_APP_SERIES ? valor : false;

export const getConfiguracaoRelatorio: State => ConfiguracaoDeRelatorio = createSelector(
  getDataBaseFormatadaBanco,
  getTipoDataBaseSelecionada,
  medidasLimpas,
  getPeriodosSelecionados,
  getPeriodicidadeSelecionada,
  getModoApp,
  getAjusteDiasSelecionado,
  getDataInicialSelecionada,
  getDataFinalSelecionada,
  identificadoresSeries,
  exibirDatasSemNegociacaoSeries,
  (
    dataBase,
    tipoDataBase,
    medidasSelecionadas,
    periodosSelecionados,
    periodicidadeSelecionada,
    modoApp,
    ajusteDias,
    dataInicial,
    dataFinal,
    identificadoresSeries,
    exibirDatasSemNegociacaoSeries
  ) => ({
    dataBase,
    tipoDataBase,
    medidasSelecionadas,
    periodosSelecionados,
    periodicidadeSelecionada,
    modoApp,
    ajusteDias,
    dataInicial: dataInicial && dataInicial.format(FORMATO_DATA_BANCO),
    dataFinal: dataFinal && dataFinal.format(FORMATO_DATA_BANCO),
    identificadoresSeries,
    exibirDatasSemNegociacaoSeries,
  })
);

const getHashConfiguracaoRelatorio = createSelector(
  getConfiguracaoRelatorio,
  config => {
    return hash(
      JSON.stringify({
        dataBase: config.dataBase,
        tipoDataBase: config.tipoDataBase,
        periodo: ifModoSeries(config.modoApp, config.periodosSelecionados[0]),
        periodicidade: ifModoSeries(
          config.modoApp,
          config.periodicidadeSelecionada
        ),
        ajusteDias: ifModoSeries(config.modoApp, config.ajusteDias),
        dataInicial: ifModoSeries(config.modoApp, config.dataInicial),
        dataFinal: ifModoSeries(config.modoApp, config.dataFinal),
        identificadoresSeries: ifModoSeries(
          config.modoApp,
          config.identificadoresSeries
        ),
        exibirDatasSemNegociacaoSeries: ifModoSeries(
          config.modoApp,
          config.exibirDatasSemNegociacaoSeries
        ),

        medidasSelecionadas: config.medidasSelecionadas.map(
          normalizaMedidaSelecionada
        ),
      })
    );
  }
);

const configuracaoResultado = createSelector(
  selectedReport,
  report => report.configuration || {}
);

const getHashConfiguracaoResultado = createSelector(
  configuracaoResultado,
  config => {
    return hash(
      JSON.stringify({
        dataBase: pathOr(
          null,
          ["configuracaoGlobalRelatorio", "dataBase"],
          config
        ),
        tipoDataBase: pathOr(
          null,
          ["configuracaoGlobalRelatorio", "tipoDataBase"],
          config
        ),
        periodo: ifModoSeries(
          config.tipoRelatorio,
          pathOr(null, ["configuracaoGlobalRelatorio", "periodo"], config)
        ),
        periodicidade: ifModoSeries(
          config.tipoRelatorio,
          pathOr(null, ["configuracaoGlobalRelatorio", "periodicidade"], config)
        ),
        ajusteDias: ifModoSeries(
          config.tipoRelatorio,
          pathOr(null, ["configuracaoGlobalRelatorio", "ajusteDias"], config)
        ),
        dataInicial: ifModoSeries(
          config.tipoRelatorio,
          pathOr(null, ["configuracaoGlobalRelatorio", "dataInicial"], config)
        ),
        dataFinal: ifModoSeries(
          config.tipoRelatorio,
          pathOr(null, ["configuracaoGlobalRelatorio", "dataFinal"], config)
        ),

        identificadoresSeries: ifModoSeries(
          config.tipoRelatorio,
          pathOr(
            null,
            ["configuracaoGlobalRelatorio", "identificadoresAtivos"],
            config
          )
        ),

        exibirDatasSemNegociacaoSeries: ifModoSeries(
          config.tipoRelatorio,
          pathOr(
            null,
            ["configuracaoGlobalRelatorio", "exibirDatasSemNegociacao"],
            config
          )
        ),

        medidasSelecionadas: (config.medidasSelecionadas || []).map(
          normalizaMedidaSelecionada
        ),
      })
    );
  }
);

export const resultadoEstaSincronizadoComConfiguracao: State => boolean = createSelector(
  getHashConfiguracaoResultado,
  getHashConfiguracaoRelatorio,
  (hashResultado, hashConfiguracao) => hashResultado === hashConfiguracao
);

export const isRestaurandoConfiguracoes = (state: State): boolean =>
  state.restaurandoConfiguracoes;

export const getConfiguracoesRelatorioSalvo = (
  state: State
): ?ConfiguracaoGlobalRelatorio => state.configuracoesRelatorioSalvo;
